<template>
  <div class="jikangai">
    <h3>時間外申請</h3>
    <template v-if="form.jikangai">
      <div class="item">
        <b class="required">勤務日</b>
        <date-picker v-model="form.jikangai.jikangai_date" :error="getError(form.jikangai, 'jikangai_date')"
          @update:modelValue="updateKinmuDate"></date-picker>
      </div>

      <div class="item">
        <koyouKeiyakuTimeSelect v-if="isInit" :errors="form.errors" :targetDate="form.jikangai.jikangai_date"
          :koyouKeiyakuId="koyouKeiyakuId" :workTimeId="workTimeId" @change="changeKoyouKeiyakuTime">
        </koyouKeiyakuTimeSelect>
      </div>

      <div class="item">
        <b class="required">時間外勤務開始日時</b>
        <time-picker v-model="form.jikangai.start_time" :startTime="{ minutes: 0 }"
          :error="getError(form.jikangai, 'start_time')"></time-picker>
      </div>
      <div class="item">
        <b class="required">時間外勤務終了日時</b>
        <time-picker v-model="form.jikangai.end_time" :startTime="{ minutes: 0 }"
          :error="getError(form.jikangai, 'end_time')"></time-picker>
      </div>
      <div class="item">
        <b>医療機関の終了時間</b>
        <time-picker v-model="form.jikangai.iin_close_time" :minutesIncrement="5" :startTime="{ minutes: 0 }"
          :error="getError(form.jikangai, 'iin_close_time')"></time-picker>
      </div>
      <div class="item">
        <b class="required">業務内容（詳細に）</b>
        <input-textarea v-model="form.jikangai.gyoumu_notes"
          :error="getError(form.jikangai, 'gyoumu_notes')"></input-textarea>
      </div>
      <div class="item">
        <b>備考</b>
        <input-textarea v-model="form.notes" :error="getError(form, 'notes')"></input-textarea>
      </div>
    </template>
  </div>
</template>

<script lang="ts">

import { defineComponent, watch, toRefs, nextTick, onMounted, ref, Ref } from 'vue';
import { useJikangai } from '@/composable/shinsei/jikangai/jikangai';
import { useForm } from '@/composable/form/form';
import { Shinsei, ShinseiKoyouKeiyakuTime } from '@/types/Shinsei';
import { KoyouKeiyakuTimeSelectEmitData, SyuugyouBasyoAndKoyouKeiyakuOption } from '@/types/SyuugyouBasyo';
import KoyouKeiyakuTimeSelect from "@/components/shinsei/create/parts/KoyouKeiyakuTimeSelect.vue"
import { isString } from "lodash";
import { Logger } from '@/utils/Logger';

export default defineComponent({
  props: {
    form: {
      type: Object as () => Shinsei,
      default: null
    },
    selectSyuugyouBasyo: {
      type: Object as () => SyuugyouBasyoAndKoyouKeiyakuOption | null,
      default: null
    }
  },
  emits: {
    changeKoyouKeiyakuTime: (selectedSyuugyouBasyo: SyuugyouBasyoAndKoyouKeiyakuOption | null, selectedKoyouKeiyakuTimeOption: ShinseiKoyouKeiyakuTime | null) => true,
  },
  setup(props, context) {
    // propsをref化することで変更を検知できるようにする
    const { selectSyuugyouBasyo, form } = toRefs(props)
    // エラー取得
    const { getError } = useForm()
    const { confAlert, selectWorkTime } = useJikangai()

    // 編集モードで使用する就業場所（雇用契約）と就業時間の初期値
    // 初期化時のみ使用し、以降は使用せずnullにする（実データはリクエストで送信される申請オブジェクト内に保持されるため）
    let workTimeId: Ref<number | null> = ref(null)
    let koyouKeiyakuId: Ref<number | null> = ref(null)

    const isInit = ref(false)

    onMounted(async () => {
      Logger.debug('----------------------------\n時間外申請コンポーネントの初期化開始')
      Logger.debug('時間外申請 onMounted', { '申請': form.value })
      nextTick(() => {
        Logger.debug('時間外申請 onMounted->nextTick', { '申請': form.value })
        if (!form.value.jikangai) {
          Logger.debug('新規作成モードの初期化を行います')
          form.value.jikangai = {
            jikangai_date: null,
            start_datetime: null,
            end_datetime: null,
            start_date: null,
            start_time: null,
            end_date: null,
            end_time: null,
            gyoumu_notes: null,
            iin_close_time: null,
          }
        } else {
          Logger.debug('編集モードの初期化を行います')
          workTimeId.value = form.value.shinsei_koyou_keiyaku_time.length > 0 ? form.value.shinsei_koyou_keiyaku_time[0].koyou_keiyaku_time_id : null
          koyouKeiyakuId.value = form.value.koyou_keiyaku_id ?? null
          Logger.debug('時間外申請より就業場所（雇用契約）と就業時間を初期化しました', { '雇用契約ID': koyouKeiyakuId.value, '就業時間ID': workTimeId.value })
        }
        isInit.value = true
        Logger.debug('時間外申請コンポーネントの初期化完了\n----------------------------')
      })
    })

    const updateKinmuDate = (val: string | Date | null) => {
      if (!form.value.jikangai) return

      // 勤務日が変更された場合に、時間外開始日/終了日にも同日をセットする
      form.value.jikangai.start_date = val
      form.value.jikangai.end_date = val
    }

    const alertStartTime = () => {
      if (!form.value.jikangai) return

      if (form.value.shinsei_koyou_keiyaku_time.length > 0) {
        return confAlert(form.value.jikangai)
      } else {
        return true
      }
    }

    watch(() => form.value.shinsei_koyou_keiyaku_time, () => {
      Logger.debug('[watch] 申請雇用契約時間の変更が検知されました', form.value.shinsei_koyou_keiyaku_time)
      // 親コンポーネントにてform.value.shinsei_koyou_keiyaku_timeが変更された場合に走らせるが、その場合selectSyuugyouBasyoも同時に変更されている
      // タイミングによってはselectSyuugyouBasyoがpropsに乗って渡ってくる（＝このコンポーネントで利用可能になる）前に走ってしまうケースがある
      // そのためここではすべての変更が終わった後に実行するためnextTickで実行している
      nextTick(() => {
        setStartDate()
      })
    });

    // 選択した日の就業時間（開始、終了）を取得し、時間外開始の時間を画面に設定する
    const setStartDate = () => {
      if (!form.value.jikangai) return

      Logger.debug('申請雇用契約時間をもとに時間外勤務開始日時、終了日時のセット開始', { "申請雇用契約時間": form.value.shinsei_koyou_keiyaku_time, "選択中の就業場所": selectSyuugyouBasyo.value })
      // 申請用の雇用契約時間と選択済みの就業場所が存在する場合
      if (form.value.shinsei_koyou_keiyaku_time.length > 0 && selectSyuugyouBasyo.value) {

        // 選択中の就業場所（雇用契約）に含まれる時間帯と、申請雇用契約時間の雇用契約IDが一致するものを取り出す
        const selectData = selectSyuugyouBasyo.value.work_times?.find(function (v, k) {
          return v.koyou_keiyaku_time_id == form.value.shinsei_koyou_keiyaku_time[0].koyou_keiyaku_time_id
        })

        // 雇用契約時間IDが一致するデータがある場合
        if (selectData) {
          form.value.jikangai.start_time = selectData.end_time
          selectWorkTime.startTime = selectData.start_time
          selectWorkTime.endTime = selectData.end_time
          Logger.debug('申請雇用契約時間をもとに時間外勤務開始日時、終了日時がセットされました')
        } else {
          // 選択した日が早番遅番の場合かどうかチェック
          let hayabanOsobanSelectData = selectSyuugyouBasyo.value.hayaban_osobans?.find(function (v, k) {
            return v.target_dakoku_date == form.value.shinsei_koyou_keiyaku_time[0].target_date
          })

          // 早番遅番データがある場合
          if (hayabanOsobanSelectData) {

            // 早番遅番の勤務開始予定時間
            if (isString(hayabanOsobanSelectData.end_datetime)) {
              let tmpEndTime = new Date(hayabanOsobanSelectData.end_datetime.replace(/-/g, "/"));

              // 時間部分を取得
              let tmpEndLocaleTime = tmpEndTime.toLocaleTimeString()

              form.value.jikangai.start_time = tmpEndLocaleTime
              selectWorkTime.endTime = tmpEndLocaleTime
            }

            // 早番遅番の勤務終了予定時間
            if (isString(hayabanOsobanSelectData.start_datetime)) {
              let tmpStartTime = new Date(hayabanOsobanSelectData.start_datetime.replace(/-/g, "/"));

              // 時間部分を取得
              let tmpStartLocaleTime = tmpStartTime.toLocaleTimeString()
              selectWorkTime.startTime = tmpStartLocaleTime
            }
            Logger.debug('申請雇用契約時間をもとに時間外勤務開始日時、終了日時がセットされました（早番・遅番の場合）')

          } else {
            form.value.jikangai.start_time = null
            selectWorkTime.startTime = null
            selectWorkTime.endTime = null
            Logger.debug('申請雇用契約時間をもとに時間外勤務開始日時、終了日時がクリアされました（早番・遅番の場合）')
          }

        }
      } else {
        form.value.jikangai.start_time = null
        selectWorkTime.startTime = null
        selectWorkTime.endTime = null
        Logger.debug('申請雇用契約時間をもとに時間外勤務開始日時、終了日時がクリアされました')
      }
    }

    // 就業場所・就業時間が変更された場合に走る処理
    // 引数は以下コンポーネントのemits.changeに準拠
    // src/resources/ts/components/shinsei/create/parts/KoyouKeiyakuTimeSelect.vue
    const changeKoyouKeiyakuTime = (emitData: KoyouKeiyakuTimeSelectEmitData) => {
      if (!form.value.jikangai) return

      let koyouKeiyakuTime: ShinseiKoyouKeiyakuTime | null = null

      // 就業時間に関する情報のセット
      if (emitData.selectedKoyouKeiyakuTimeOption) {
        koyouKeiyakuTime = {
          shinsei_id: form.value.shinsei_id,
          koyou_keiyaku_time_id: emitData.selectedKoyouKeiyakuTimeOption.val,
          target_date: emitData.targetDate,
          tsuujouTime: null,
          isHannichi: null,
          canOnlyAmKinmu: null,
          canOnlyPmKinmu: null,
        }
      }

      koyouKeiyakuId.value = emitData.selectedSyuugyouBasyo?.koyou_id ?? null
      workTimeId.value = emitData.selectedKoyouKeiyakuTimeOption?.val ?? null

      // 就業場所に関する情報をemitする（申請オブジェクトの変更等は親に移譲する）
      context.emit('changeKoyouKeiyakuTime', emitData.selectedSyuugyouBasyo, koyouKeiyakuTime);
    }

    return {
      getError,
      updateKinmuDate,
      alertStartTime,
      changeKoyouKeiyakuTime,
      workTimeId,
      koyouKeiyakuId,
      form,
      isInit
    }
  },
  components: {
    'koyouKeiyakuTimeSelect': KoyouKeiyakuTimeSelect
  },
})

</script>
<style lang="scss" scoped>
@import '@scss/components/shinsei-create.scss';
</style>
